import * as q from './queue';

/**
 * Drives the queue.
 *
 * Small files go in one PUT. Anything over the part size is a multipart upload
 * whose finished parts are written back to IndexedDB as they land — so a phone
 * that dies three parts into a 200 MB video picks up at part four rather than
 * starting again.
 *
 * Nothing here throws at the caller. A failure leaves the item in the queue
 * with its attempt count raised, and the next drain tries again.
 */

const CONCURRENCY = 2;
const MAX_ATTEMPTS = 6;

type Listener = (items: q.QueueItem[]) => void;

export class Uploader {
  private running = false;
  private stopped = false;
  private listeners = new Set<Listener>();

  constructor(
    private slug: string,
    private partSize: number,
  ) {}

  onChange(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private async emit() {
    const items = await q.all(this.slug);
    for (const fn of this.listeners) fn(items);
  }

  /** Safe to call as often as you like; only one drain runs at a time. */
  async drain(): Promise<void> {
    if (this.running || this.stopped) return;
    this.running = true;
    try {
      for (;;) {
        const items = await q.all(this.slug);
        const pending = items.filter(
          (i) => (i.state === 'queued' || i.state === 'uploading') && i.attempts < MAX_ATTEMPTS,
        );
        if (pending.length === 0) break;
        if (!navigator.onLine) break;

        const batch = pending.slice(0, CONCURRENCY);
        await Promise.all(batch.map((i) => this.one(i)));
        await this.emit();
      }
    } finally {
      this.running = false;
      await this.emit();
    }
  }

  stop() {
    this.stopped = true;
  }

  private async one(item: q.QueueItem): Promise<void> {
    try {
      const blob = await q.getBlob(item);
      if (!blob) {
        // The bytes are gone (cleared storage, evicted). Nothing to retry with,
        // so say so plainly rather than spinning forever on an empty file.
        await q.put({ ...item, state: 'failed', attempts: 99, error: 'bytes missing' });
        await this.emit();
        return;
      }

      await q.put({ ...item, state: 'uploading' });
      await this.emit();

      if (item.size <= this.partSize) {
        await this.single(item, blob);
      } else {
        await this.multipart(item, blob);
      }

      await q.shed(item.id);
    } catch (err) {
      const attempts = item.attempts + 1;
      const current = (await q.get(item.id)) ?? item;
      await q.put({
        ...current,
        state: attempts >= MAX_ATTEMPTS ? 'failed' : 'queued',
        attempts,
        error: String(err).slice(0, 200),
      });
      // Back off before the next pass, so a flaky connection is not hammered.
      await sleep(Math.min(1000 * 2 ** attempts, 20000));
    }
  }

  private async single(item: q.QueueItem, blob: Blob): Promise<void> {
    const res = await fetch(`/api/e/${this.slug}/file/${item.id}`, {
      method: 'PUT',
      headers: {
        'content-type': item.type,
        'x-filename': encodeURIComponent(item.filename),
        'x-grant': JSON.stringify(item.grant),
      },
      body: blob,
    });
    if (!res.ok) throw new Error(`upload ${res.status}`);
    await q.put({ ...item, state: 'done', sent: item.size });
  }

  private async multipart(item: q.QueueItem, blob: Blob): Promise<void> {
    let state = { ...item };

    if (!state.uploadId) {
      const res = await fetch(`/api/e/${this.slug}/mpu/${state.id}/start`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ filename: state.filename, contentType: state.type, size: state.size }),
      });
      if (!res.ok) throw new Error(`start ${res.status}`);
      const { uploadId } = (await res.json()) as { uploadId: string };
      state = { ...state, uploadId, parts: [], nextPart: 1, sent: 0 };
      await q.put(state);
    }

    const total = Math.ceil(state.size / this.partSize);
    for (let n = state.nextPart; n <= total; n++) {
      const start = (n - 1) * this.partSize;
      const chunk = blob.slice(start, Math.min(start + this.partSize, state.size));
      const res = await fetch(
        `/api/e/${this.slug}/mpu/${state.id}/part/${n}?uploadId=${encodeURIComponent(state.uploadId!)}`,
        { method: 'PUT', body: chunk },
      );
      if (!res.ok) throw new Error(`part ${n} ${res.status}`);
      const part = (await res.json()) as { partNumber: number; etag: string };

      // Persist progress after every part, before the next one starts: this is
      // what makes a resume possible, and it only works because the record is
      // small now. Writing the blob here cost 71 MB a part and lost the race.
      state = {
        ...state,
        parts: [...state.parts, part],
        nextPart: n + 1,
        sent: Math.min(start + this.partSize, state.size),
      };
      await q.put(state);
      await this.emit();
    }

    const res = await fetch(`/api/e/${this.slug}/mpu/${state.id}/complete`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ uploadId: state.uploadId, parts: state.parts, grant: state.grant }),
    });
    if (!res.ok) throw new Error(`complete ${res.status}`);
    await q.put({ ...state, state: 'done', sent: state.size });
  }
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}
